import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Lock, Unlock, TrendingUp, Users } from 'lucide-react';
import { StakeModal } from './StakeModal';
import { UnstakeModal } from './UnstakeModal';
import { StakingPool } from '../../types/wallet';

const mockPools: StakingPool[] = [
  {
    id: 'ndh-flexible',
    name: 'NDH Flexible Staking',
    token: 'NDH',
    apy: 12.5,
    tvl: '1,250,000',
    userStaked: '1,500',
    pendingRewards: '45.67',
    lockPeriod: 0,
    isActive: true,
  },
  {
    id: 'ndh-30-day',
    name: 'NDH Fixed 30-Day Lock',
    token: 'NDH',
    apy: 18.2,
    tvl: '2,840,000',
    userStaked: '5,000',
    pendingRewards: '156.23',
    lockPeriod: 30,
    isActive: true,
  },
  {
    id: 'ndh-90-day',
    name: 'NDH Fixed 90-Day Lock',
    token: 'NDH',
    apy: 25.8,
    tvl: '890,000',
    userStaked: '0',
    pendingRewards: '0',
    lockPeriod: 90,
    isActive: true,
  },
  {
    id: 'ndh-eth-lp',
    name: 'NDH-ETH LP Staking',
    token: 'NDH-ETH LP',
    apy: 35.4,
    tvl: '567,000',
    userStaked: '0',
    pendingRewards: '0',
    lockPeriod: 0,
    isActive: true,
  },
];

export function StakingPools() {
  const [selectedPool, setSelectedPool] = useState<StakingPool | null>(null);
  const [modalType, setModalType] = useState<'stake' | 'unstake' | null>(null);

  const handleStake = (pool: StakingPool) => {
    setSelectedPool(pool);
    setModalType('stake');
  };

  const handleUnstake = (pool: StakingPool) => {
    setSelectedPool(pool);
    setModalType('unstake');
  };

  const closeModal = () => {
    setSelectedPool(null);
    setModalType(null);
  };

  return (
    <>
      <div className="grid gap-6">
        {mockPools.map((pool) => {
          const userStaked = parseFloat(pool.userStaked);
          const hasStake = userStaked > 0;
          const tvlProgress = (parseFloat(pool.tvl.replace(',', '')) / 5000000) * 100; // Max 5M TVL

          return (
            <Card key={pool.id} className="overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold">
                      {pool.token.split('-')[0].slice(0, 2)}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{pool.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          {pool.apy}% APY
                        </Badge>
                        {pool.lockPeriod > 0 ? (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Lock className="h-3 w-3" />
                            {pool.lockPeriod} days
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Unlock className="h-3 w-3" />
                            Flexible
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{pool.apy}%</div>
                    <div className="text-sm text-slate-500">APY</div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Pool Stats */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-center">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">Total Staked</div>
                    <div className="font-semibold">${pool.tvl}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">Your Stake</div>
                    <div className="font-semibold">{pool.userStaked} {pool.token.split('-')[0]}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">Rewards</div>
                    <div className="font-semibold text-green-600">{pool.pendingRewards}</div>
                  </div>
                </div>

                {/* Pool Capacity */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Pool Capacity</span>
                    <span>{tvlProgress.toFixed(1)}%</span>
                  </div>
                  <Progress value={tvlProgress} className="h-2" />
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {hasStake ? (
                    <>
                      <Button 
                        onClick={() => handleStake(pool)}
                        className="flex-1"
                      >
                        Add More
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handleUnstake(pool)}
                        className="flex-1"
                      >
                        Unstake
                      </Button>
                      {parseFloat(pool.pendingRewards) > 0 && (
                        <Button variant="secondary">
                          Claim {parseFloat(pool.pendingRewards).toFixed(2)}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button 
                      onClick={() => handleStake(pool)}
                      className="w-full"
                    >
                      Start Staking
                    </Button>
                  )}
                </div>

                {/* Additional Info */}
                {pool.lockPeriod > 0 && hasStake && (
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                      <Lock className="h-4 w-4" />
                      <span className="text-sm">
                        Tokens locked until {new Date(Date.now() + pool.lockPeriod * 24 * 60 * 60 * 1000).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Modals */}
      {selectedPool && modalType === 'stake' && (
        <StakeModal 
          pool={selectedPool} 
          onClose={closeModal} 
        />
      )}
      {selectedPool && modalType === 'unstake' && (
        <UnstakeModal 
          pool={selectedPool} 
          onClose={closeModal} 
        />
      )}
    </>
  );
}